import { useEffect, useState, useCallback } from "react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";
import { useTaxonomy } from "@/context/TaxonomyContext";
import MultiSelect from "@/components/MultiSelect";
import { MatchScore, MatchBadge } from "@/components/MatchScore";
import { toast } from "sonner";
import { Search, Star, Mail, Download, FileDown, Save, Filter, MapPin, Briefcase, IndianRupee, X, CheckCircle2, XCircle } from "lucide-react";

const empty = {
  keyword: "", min_experience: "", max_experience: "", min_cable_experience: "",
  education: [], departments: [], designations: [], products: [], processes: [], skills: [],
  industries: [], conductors: [], locations: [], notice_periods: [], tags: [],
  current_company: "", min_expected_salary: "", max_expected_salary: "",
  willing_to_relocate: false, verified: false, resume_available: false,
};

export default function AdminCandidateSearch() {
  const tax = useTaxonomy();
  const [f, setF] = useState(empty);
  const [res, setRes] = useState({ candidates: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("best_match");
  const [selected, setSelected] = useState([]);
  const [drawer, setDrawer] = useState(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const buildBody = useCallback((pg) => {
    const body = { page: pg, page_size: 10, sort };
    Object.entries(f).forEach(([k, v]) => {
      if (Array.isArray(v)) { if (v.length) body[k] = v; }
      else if (typeof v === "boolean") { if (v) body[k] = v; }
      else if (v !== "" && v != null) { body[k] = ["min_experience", "max_experience", "min_cable_experience", "min_expected_salary", "max_expected_salary"].includes(k) ? parseFloat(v) : v; }
    });
    return body;
  }, [f, sort]);

  const search = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const r = await api.post("/admin/candidates/search", buildBody(pg));
      setRes(r.data); setPage(pg);
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  }, [buildBody]);

  useEffect(() => { search(1); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(() => search(1), 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [f, sort]);

  const shortlist = async (id) => { const r = await api.post(`/admin/candidates/${id}/shortlist`, {}); toast.success(r.data.shortlisted ? "Shortlisted" : "Removed from shortlist"); };
  const contact = async (id) => { await api.post(`/admin/candidates/${id}/contact`, { subject: "A recruiter is interested in your profile", body: "We would like to connect regarding an opportunity.", type: "message" }); toast.success("Message sent to candidate"); };

  const exportCsv = async () => {
    const body = selected.length ? { candidate_ids: selected } : { filters: buildBody(1) };
    const r = await api.post("/admin/candidates/export", body, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "candidates.csv"; a.click(); URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  const saveSearch = async () => {
    const name = prompt("Name this search:");
    if (!name) return;
    await api.post("/admin/saved-searches", { name, filters: buildBody(1) });
    toast.success("Search saved");
  };

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const totalPages = Math.ceil(res.total / 10);

  return (
    <AdminShell>
      <div className="grid xl:grid-cols-[300px_1fr] gap-5">
        {/* FILTERS */}
        <aside className="bg-white rounded-xl border border-slate-200 p-4 h-fit xl:sticky xl:top-4 space-y-4 max-h-[calc(100vh-3rem)] overflow-y-auto scrollbar-thin">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-slate-900 flex items-center gap-2"><Filter className="w-4 h-4 text-[#D96B27]" />Filters</h2>
            <button onClick={() => setF(empty)} className="text-xs text-[#D96B27] font-medium">Reset</button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Keyword</label>
            <div className="flex items-center gap-2 border border-slate-300 rounded-md px-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input data-testid="candidate-search-input" value={f.keyword} onChange={(e) => set("keyword", e.target.value)} placeholder="Name, designation, skill" className="w-full py-2 text-sm outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Min Exp</label><input type="number" data-testid="filter-min-exp" value={f.min_experience} onChange={(e) => set("min_experience", e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2" /></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Max Exp</label><input type="number" value={f.max_experience} onChange={(e) => set("max_experience", e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2" /></div>
          </div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Min Cable Exp (yrs)</label><input type="number" data-testid="filter-cable-exp" value={f.min_cable_experience} onChange={(e) => set("min_cable_experience", e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2" /></div>

          <MultiSelect label="Education" options={tax.education_degrees || []} value={f.education} onChange={(v) => set("education", v)} testid="filter-education" />
          <MultiSelect label="Department" options={tax.departments || []} value={f.departments} onChange={(v) => set("departments", v)} testid="filter-dept" />
          <MultiSelect label="Designation" options={tax.designations || []} value={f.designations} onChange={(v) => set("designations", v)} testid="filter-designation" />
          <MultiSelect label="Product Experience" options={tax.products || []} value={f.products} onChange={(v) => set("products", v)} testid="filter-products" />
          <MultiSelect label="Process Experience" options={tax.all_processes || []} value={f.processes} onChange={(v) => set("processes", v)} testid="filter-processes" />
          <MultiSelect label="Skills" options={tax.skills || []} value={f.skills} onChange={(v) => set("skills", v)} testid="filter-skills" />
          <MultiSelect label="Conductor" options={tax.conductors || []} value={f.conductors} onChange={(v) => set("conductors", v)} testid="filter-conductors" />
          <MultiSelect label="Location" options={tax.locations || []} value={f.locations} onChange={(v) => set("locations", v)} testid="filter-locations" />
          <MultiSelect label="Notice Period" options={tax.notice_periods || []} value={f.notice_periods} onChange={(v) => set("notice_periods", v)} testid="filter-notice" />
          <MultiSelect label="Tags" options={tax.default_tags || []} value={f.tags} onChange={(v) => set("tags", v)} testid="filter-tags" />

          <div><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Current Company</label><input value={f.current_company} onChange={(e) => set("current_company", e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2" /></div>

          <div className="space-y-2 pt-1">
            {[["willing_to_relocate", "Willing to relocate"], ["verified", "Verified only"], ["resume_available", "Resume available"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" data-testid={`filter-${k}`} checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="accent-[#D96B27]" /> {l}
              </label>
            ))}
          </div>
        </aside>

        {/* RESULTS */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="results-count">{res.total} Candidates Found</h1>
              <p className="text-xs text-slate-500">{selected.length} selected</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="text-sm border border-slate-300 rounded-md px-3 py-2 bg-white">
                <option value="best_match">Best Match</option>
                <option value="experience">Experience</option>
                <option value="recent">Recently Updated</option>
              </select>
              <button onClick={saveSearch} data-testid="save-search-btn" className="flex items-center gap-1.5 text-sm border border-slate-300 rounded-md px-3 py-2 bg-white hover:border-[#D96B27]"><Save className="w-4 h-4" />Save</button>
              <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-1.5 text-sm copper-gradient text-white rounded-md px-3 py-2"><FileDown className="w-4 h-4" />Export CSV</button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-36 bg-white rounded-xl border border-slate-200 animate-pulse" />)}</div>
          ) : res.candidates.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-slate-200 text-slate-500">No candidates match your filters.</div>
          ) : (
            <div className="space-y-3">
              {res.candidates.map((c) => (
                <div key={c.candidate_id} data-testid={`candidate-card-${c.candidate_id}`} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#D96B27] transition-colors">
                  <div className="flex gap-4">
                    <input type="checkbox" checked={selected.includes(c.candidate_id)} onChange={() => toggleSelect(c.candidate_id)} className="mt-1 accent-[#D96B27]" data-testid={`select-${c.candidate_id}`} />
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0 font-display font-bold text-slate-500">{c.full_name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">{c.full_name}{c.verified && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}</h3>
                          <p className="text-sm text-slate-500">{c.current_designation} · {c.current_company}</p>
                        </div>
                        {c.match_score != null && <div className="text-center shrink-0"><MatchScore score={c.match_score} /><div className="mt-1"><MatchBadge band={c.match_band} /></div></div>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
                        <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5 text-slate-400" />{c.total_experience} yrs ({c.cable_experience} cable)</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400" />{c.current_location}</span>
                        <span>{c.highest_education} {c.education_specialization}</span>
                        {c.expected_salary && <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5 text-slate-400" />{c.expected_salary} LPA</span>}
                        <span>Notice: {c.notice_period || "—"}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(c.products || []).slice(0, 4).map((p) => <span key={p} className="text-xs bg-[#FFF3EC] text-[#C05615] px-1.5 py-0.5 rounded">{p}</span>)}
                        {(c.skills || []).slice(0, 4).map((p) => <span key={p} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{p}</span>)}
                      </div>
                      {c.match_reasons?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                          {c.match_reasons.slice(0, 4).map((r, i) => <span key={i} className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{r}</span>)}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => setDrawer(c)} data-testid={`view-${c.candidate_id}`} className="text-xs font-medium border border-slate-300 rounded-md px-3 py-1.5 hover:border-[#D96B27]">View Profile</button>
                        <button onClick={() => shortlist(c.candidate_id)} data-testid={`shortlist-${c.candidate_id}`} className="text-xs font-medium border border-slate-300 rounded-md px-3 py-1.5 hover:border-[#D96B27] flex items-center gap-1"><Star className="w-3.5 h-3.5" />Shortlist</button>
                        <button onClick={() => contact(c.candidate_id)} data-testid={`contact-${c.candidate_id}`} className="text-xs font-medium border border-slate-300 rounded-md px-3 py-1.5 hover:border-[#D96B27] flex items-center gap-1"><Mail className="w-3.5 h-3.5" />Contact</button>
                        {c.resume && <a href={`${api.defaults.baseURL}/files/${c.resume.storage_path}?download=true`} target="_blank" rel="noreferrer" className="text-xs font-medium border border-slate-300 rounded-md px-3 py-1.5 hover:border-[#D96B27] flex items-center gap-1"><Download className="w-3.5 h-3.5" />Resume</a>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-5">
              <button disabled={page === 1} onClick={() => search(page - 1)} className="px-4 py-2 text-sm border rounded-md bg-white disabled:opacity-40">Prev</button>
              <span className="px-4 py-2 text-sm">Page {page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => search(page + 1)} className="px-4 py-2 text-sm border rounded-md bg-white disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      </div>

      {drawer && <CandidateDrawer candidate={drawer} onClose={() => setDrawer(null)} />}
    </AdminShell>
  );
}

function CandidateDrawer({ candidate, onClose }) {
  const tax = useTaxonomy();
  const [c, setC] = useState(candidate);
  const [note, setNote] = useState("");

  useEffect(() => { api.get(`/admin/candidates/${candidate.candidate_id}/full`).then((r) => setC(r.data)).catch(() => {}); }, [candidate]);

  const addNote = async () => {
    if (!note.trim()) return;
    const r = await api.post(`/admin/candidates/${c.candidate_id}/notes`, { text: note });
    setC((s) => ({ ...s, notes: [...(s.notes || []), r.data] })); setNote(""); toast.success("Note added");
  };
  const setTags = async (tags) => { await api.post(`/admin/candidates/${c.candidate_id}/tags`, { tags }); setC((s) => ({ ...s, tags })); };
  const action = async (a) => { await api.post(`/admin/candidates/${c.candidate_id}/action`, { action: a }); toast.success(`Candidate ${a}d`); setC((s) => ({ ...s, verified: a === "verify" ? true : s.verified, status: a === "deactivate" ? "Inactive" : s.status })); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="candidate-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto scrollbar-thin shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-slate-900">Candidate Profile</h2>
          <button onClick={onClose} data-testid="drawer-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center font-display font-bold text-2xl text-slate-500">{c.full_name?.[0]}</div>
            <div>
              <h3 className="font-display font-bold text-xl text-slate-900">{c.full_name}</h3>
              <p className="text-slate-500 text-sm">{c.current_designation} · {c.current_company}</p>
              <p className="text-slate-400 text-xs mt-0.5">{c.email} · {c.mobile}</p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => action("verify")} className="text-xs flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-md"><CheckCircle2 className="w-3.5 h-3.5" />Verify</button>
            <button onClick={() => action("deactivate")} className="text-xs flex items-center gap-1 bg-red-50 text-red-700 px-3 py-1.5 rounded-md"><XCircle className="w-3.5 h-3.5" />Deactivate</button>
            {c.resume && <a href={`${api.defaults.baseURL}/files/${c.resume.storage_path}?download=true`} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" />Resume</a>}
          </div>

          <Grid label="Experience" value={`${c.total_experience} yrs total · ${c.cable_experience} yrs cable`} />
          <Grid label="Education" value={`${c.highest_education} ${c.education_specialization}`} />
          <Grid label="Location" value={`${c.current_location}${c.willing_to_relocate ? " (open to relocate)" : ""}`} />
          <Grid label="Salary" value={`Current ${c.current_salary || "—"} → Expected ${c.expected_salary || "—"} LPA`} />
          <Grid label="Notice" value={c.notice_period} />

          <Chips title="Products" items={c.products} color="bg-[#FFF3EC] text-[#C05615]" />
          <Chips title="Processes" items={c.processes} color="bg-blue-50 text-blue-700" />
          <Chips title="Skills" items={c.skills} color="bg-slate-100 text-slate-600" />
          <Chips title="Industries" items={c.industries} color="bg-slate-100 text-slate-600" />

          <div>
            <MultiSelect label="Tags" options={tax.default_tags || []} value={c.tags || []} onChange={setTags} testid="drawer-tags" />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Internal Recruiter Notes (private)</h4>
            <div className="space-y-2 mb-2">
              {(c.notes || []).map((n) => <div key={n.id} className="bg-slate-50 rounded-md p-2 text-sm text-slate-700">{n.text}<div className="text-xs text-slate-400 mt-0.5">— {n.author}</div></div>)}
              {(!c.notes || c.notes.length === 0) && <p className="text-xs text-slate-400">No notes yet.</p>}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" data-testid="note-input" className="flex-1 text-sm border border-slate-300 rounded-md px-3 py-2" />
              <button onClick={addNote} data-testid="add-note-btn" className="copper-gradient text-white text-sm px-4 rounded-md">Add</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Grid = ({ label, value }) => (<div className="flex justify-between gap-3 text-sm border-b border-slate-50 pb-2"><span className="text-slate-400">{label}</span><span className="text-slate-700 font-medium text-right">{value}</span></div>);
const Chips = ({ title, items, color }) => (items?.length ? <div><h4 className="text-xs font-semibold text-slate-500 uppercase mb-1.5">{title}</h4><div className="flex flex-wrap gap-1">{items.map((x) => <span key={x} className={`text-xs px-2 py-0.5 rounded ${color}`}>{x}</span>)}</div></div> : null);
